import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { trackPageView } from '../lib/analytics';
import { getCloudinaryThumbnail } from '../lib/cloudinaryUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { trackPageView('/', 'Inicio'); }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, arrivalsRes] = await Promise.all([
          axios.get(`${API}/products?featured=true&status=active&limit=8`),
          axios.get(`${API}/categories?status=active`),
          axios.get(`${API}/products?status=active&sort_by=created_at&sort_order=desc&limit=4`)
        ]);
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setNewArrivals(arrivalsRes.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="bg-black min-h-screen" data-testid="home-page">

      {/* Categories - First thing the user sees */}
      <section className="pt-28 pb-6 md:pt-32 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex overflow-x-auto gap-3 md:gap-4 pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide md:grid md:grid-cols-4 md:overflow-visible">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-[42vw] md:w-auto">
                  <div className="aspect-[4/5] rounded-2xl skeleton" />
                </div>
              ))
            ) : (
              categories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.08, duration: 0.4 }}
                  className="flex-shrink-0 w-[42vw] md:w-auto"
                >
                  <Link
                    to={`/tienda/${category.slug}`}
                    className="block relative aspect-[4/5] rounded-2xl overflow-hidden group"
                  >
                    <img
                      src={category.image_url || 'https://images.unsplash.com/photo-1570976447640-ac859083963f?w=600'}
                      alt={category.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                      <h3 className="text-base md:text-lg font-semibold text-white leading-tight">
                        {category.name}
                      </h3>
                    </div>
                  </Link>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            {...fadeInUp}
            className="flex items-center justify-between mb-6 md:mb-8"
          >
            <h2 className="text-xl md:text-2xl font-semibold text-white">Destacados</h2>
            <Link
              to="/tienda"
              className="text-gold hover:text-gold-hover transition-colors flex items-center gap-1 text-sm font-medium"
            >
              Ver todo
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <div className="aspect-[3/4] rounded-xl skeleton" />
                  <div className="h-3 skeleton w-3/4 rounded" />
                  <div className="h-3 skeleton w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* New Arrivals */}
      <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            {...fadeInUp}
            className="flex items-center justify-between mb-6 md:mb-8"
          >
            <h2 className="text-xl md:text-2xl font-semibold text-white">Recién llegados</h2>
            <Link
              to="/tienda"
              className="text-gold hover:text-gold-hover transition-colors flex items-center gap-1 text-sm font-medium"
            >
              Ver todo
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <div className="aspect-[4/5] rounded-xl skeleton" />
                  <div className="h-3 skeleton w-3/4 rounded" />
                  <div className="h-3 skeleton w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {newArrivals.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  <Link
                    to={`/producto/${product.slug}`}
                    className="block group"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-[#111] mb-3">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={getCloudinaryThumbnail(product.images[0])}
                          alt={product.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/10">
                          <span className="text-4xl font-serif">D'A</span>
                        </div>
                      )}
                      {product.category_name && (
                        <span className="absolute top-3 left-3 bg-black/50 backdrop-blur-md px-3 py-1 text-[11px] uppercase tracking-wider text-white/90 rounded-full">
                          {product.category_name}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm md:text-base font-medium text-white group-hover:text-gold transition-colors duration-200 mb-1 truncate">
                      {product.name}
                    </h3>
                    <span className="text-gold font-semibold text-base">
                      ${product.price.toFixed(2)}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-xl mx-auto px-4 md:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-2xl md:text-3xl font-serif text-white mb-3">
              ¿Necesitas ayuda?
            </h2>
            <p className="text-white/50 mb-6 text-sm leading-relaxed">
              Escríbenos por WhatsApp y te ayudamos a elegir la pieza perfecta para ti.
            </p>
            <a
              href="https://wa.me/584242925792?text=¡Hola!%20Quiero%20información%20sobre%20los%20productos%20de%20D'Avocado"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-3 rounded-full text-sm font-medium transition-colors"
            >
              Escribir por WhatsApp
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
